import axios from 'axios';
import { GoAuthApi } from './goApi.js';

// ENVIRONMENT AGNOSTIC: URL for Go API via runtime configuration
const getGoApiUrl = () => {
  // Priority 1: Electron configuration (QUER_CONFIG)
  if (window.QUER_CONFIG && window.QUER_CONFIG.apiURL) {
    return window.QUER_CONFIG.apiURL;
  }

  // Priority 2: Application configuration (APP_CONFIG)
  if (window.APP_CONFIG && window.APP_CONFIG.apiEndpoints && window.APP_CONFIG.apiEndpoints.go) {
    const goEndpoint = window.APP_CONFIG.apiEndpoints.go;
    if (goEndpoint.startsWith('http')) {
      return goEndpoint;
    }
    const baseUrl = window.APP_CONFIG.baseUrl || window.location.origin;
    return baseUrl + goEndpoint;
  }

  // Priority 3: config.json (standard runtime configuration)
  if (window.CONFIG && window.CONFIG.apiEndpoints?.go) {
    const goEndpoint = window.CONFIG.apiEndpoints.go;
    // If apiEndpoints.go is already a full URL, use it directly
    if (goEndpoint.startsWith('http')) {
      return goEndpoint;
    }
    // Otherwise, concatenate with baseUrl or use window.location.origin
    const baseUrl = window.CONFIG.baseUrl || window.location.origin;
    return baseUrl + goEndpoint;
  }

  // CRITICAL: Check if config should be loaded but isn't yet
  if (!window.__CONFIG_LOADED) {
    console.warn('[UserSettingsApi] Config not yet loaded, cannot construct API URL');
    return null; // Return null to signal config not ready
  }

  // Fallback: Use location.origin + /api for relative paths
  console.warn('[UserSettingsApi] No config found, using fallback URL');
  return window.location.origin + '/api';
};

/**
 * User Settings API for calculator settings synchronization
 * Provides functions to get and update user calculator settings from the backend
 */
export class UserSettingsApi {
  static SETTINGS_CACHE_TIME = 300000; // 5 minutes cache
  static cachedSettings = null;
  static lastSettingsCheck = 0;
  static settingsRequestInProgress = false;

  /**
   * Wait for config to be loaded (handles race condition)
   * @param {number} maxWaitMs Maximum time to wait in milliseconds
   * @returns {Promise<boolean>} True if config loaded, false if timeout
   */
  static async waitForConfig(maxWaitMs = 3000) {
    const startTime = Date.now();
    while (!window.__CONFIG_LOADED && (Date.now() - startTime) < maxWaitMs) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return !!window.__CONFIG_LOADED;
  }

  /**
   * Get user calculator settings from the API
   * @returns {Promise<Object|null>} User settings object or null if not found/error
   */
  static async getUserSettings() {
    try {
      // Check cache first
      const now = Date.now();
      if (this.cachedSettings && (now - this.lastSettingsCheck < this.SETTINGS_CACHE_TIME)) {
        console.log('[UserSettingsApi] Using cached settings');
        return this.cachedSettings;
      }

      // Prevent multiple simultaneous requests
      if (this.settingsRequestInProgress) {
        console.log('[UserSettingsApi] Settings request already in progress, using cache');
        return this.cachedSettings;
      }

      this.settingsRequestInProgress = true;

      // Get authentication token
      const token = await GoAuthApi.getAuthToken();
      if (!token) {
        console.log('[UserSettingsApi] No auth token available, cannot fetch settings');
        return null;
      }

      console.log('[UserSettingsApi] Fetching user settings from API');

      // Check if config is ready before making request
      let apiUrl = getGoApiUrl();
      if (!apiUrl) {
        console.log('[UserSettingsApi] Config not ready, waiting...');
        const configLoaded = await this.waitForConfig(3000);
        if (!configLoaded) {
          console.error('[UserSettingsApi] Config still not available after waiting');
          return null;
        }
        apiUrl = getGoApiUrl();
        if (!apiUrl) {
          console.error('[UserSettingsApi] API URL still unavailable after config load');
          return null;
        }
      }

      const response = await axios.get(`${apiUrl}/user/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        withCredentials: true,
        timeout: 10000, // 10 second timeout
        validateStatus: function (status) {
          // Accept 200-299 and 404 (no settings found)
          return (status >= 200 && status < 300) || status === 404;
        }
      });

      if (response.status === 404) {
        console.log('[UserSettingsApi] No user settings found on server');
        return null;
      }

      // Validate response data structure
      if (!response.data || typeof response.data !== 'object') {
        console.warn('[UserSettingsApi] Invalid settings response format');
        return null;
      }

      // Update cache
      this.cachedSettings = response.data;
      this.lastSettingsCheck = now;

      console.log('[UserSettingsApi] Successfully fetched user settings');
      return response.data;

    } catch (error) {
      console.error('[UserSettingsApi] Error fetching user settings:', error);
      
      // Handle specific error cases
      if (error.response) {
        if (error.response.status === 401) {
          console.log('[UserSettingsApi] Authentication failed, settings not available');
        } else if (error.response.status === 404) {
          console.log('[UserSettingsApi] User settings not found on server');
          return null;
        } else {
          console.error('[UserSettingsApi] Server error:', error.response.status, error.response.data);
        }
      } else if (error.request) {
        console.error('[UserSettingsApi] Network error fetching settings');
      } else {
        console.error('[UserSettingsApi] Error setting up settings request:', error.message);
      }

      // Return cached settings on error if available
      return this.cachedSettings;
    } finally {
      this.settingsRequestInProgress = false;
    }
  }

  /**
   * Update user calculator settings on the server
   * @param {Object} settings Calculator settings object
   * @returns {Promise<boolean>} True if update was successful, false otherwise
   */
  static async updateUserSettings(settings) {
    try {
      // Get authentication token
      const token = await GoAuthApi.getAuthToken();
      if (!token) {
        console.log('[UserSettingsApi] No auth token available, cannot update settings');
        return false;
      }

      console.log('[UserSettingsApi] Updating user settings on server');

      // Check if config is ready before making request
      let apiUrl = getGoApiUrl();
      if (!apiUrl) {
        console.log('[UserSettingsApi] Config not ready, waiting...');
        const configLoaded = await this.waitForConfig(3000);
        if (!configLoaded) {
          console.error('[UserSettingsApi] Config still not available after waiting');
          return false;
        }
        apiUrl = getGoApiUrl();
        if (!apiUrl) {
          console.error('[UserSettingsApi] API URL still unavailable after config load');
          return false;
        }
      }

      // Prepare settings payload
      const payload = {
        settings: settings,
        updatedAt: new Date().toISOString()
      };

      await axios.put(`${apiUrl}/user/settings`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        withCredentials: true,
        timeout: 15000, // 15 second timeout for updates
        validateStatus: function (status) {
          return (status >= 200 && status < 300);
        }
      });

      // Update cache with new settings
      this.cachedSettings = settings;
      this.lastSettingsCheck = Date.now();

      console.log('[UserSettingsApi] Successfully updated user settings');
      return true;

    } catch (error) {
      console.error('[UserSettingsApi] Error updating user settings:', error);
      
      // Handle specific error cases
      if (error.response) {
        if (error.response.status === 401) {
          console.log('[UserSettingsApi] Authentication failed, cannot update settings');
        } else if (error.response.status === 413) {
          console.error('[UserSettingsApi] Settings payload too large');
        } else {
          console.error('[UserSettingsApi] Server error updating settings:', error.response.status, error.response.data);
        }
      } else if (error.request) {
        console.error('[UserSettingsApi] Network error updating settings');
      } else {
        console.error('[UserSettingsApi] Error setting up settings update request:', error.message);
      }

      return false;
    }
  }

  /**
   * Clear the settings cache (useful for logout or token refresh)
   */
  static clearCache() {
    console.log('[UserSettingsApi] Clearing settings cache');
    this.cachedSettings = null;
    this.lastSettingsCheck = 0;
    this.settingsRequestInProgress = false;
  }

  /**
   * Check if user is authenticated and can sync settings
   * @returns {boolean} True if user can sync settings
   */
  static async canSyncSettings() {
    try {
      const token = await GoAuthApi.getAuthToken();
      return !!token && GoAuthApi.isTokenValid(token);
    } catch (error) {
      console.error('[UserSettingsApi] Error checking authentication for settings sync:', error);
      return false;
    }
  }
}

export default UserSettingsApi;